import React, { useState, useEffect } from "react";
import RecyclingSubmissionPopup from "./RecyclingSubmissionPopup";
import CountUpWrapper from "../../CountUpWrapper";
import { Tooltip } from "react-tooltip";
import API from "../../Services/api";
import { toast } from "../../Services/toast";

const FALLBACK_IMAGES = [
  'https://upload.wikimedia.org/wikipedia/commons/c/c5/Havant_Household_Waste_Recycling_Centre_July_2022_%2820220711_144632_331c%29.jpg',
  'https://upload.wikimedia.org/wikipedia/commons/5/54/Entrance_to_Wellingborough_Household_Waste_Recycling_Centre_-_geograph.org.uk_-_7197966.jpg',
  'https://upload.wikimedia.org/wikipedia/commons/6/62/Ewaste.jpg',
];

function isUrl(str) {
  return str && (str.startsWith('http://') || str.startsWith('https://'));
}

const SubmitRequestPopup = ({ onClose, collectionPoint, isBookmarked, updateBookmarks, autoOpenRecycling, pendingItems }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showRecyclingPopup, setShowRecyclingPopup] = useState(!!autoOpenRecycling);
  const [visibleCount, setVisibleCount] = useState(4);
  const [bookmarkStatus, setBookmarkStatus] = useState(isBookmarked);
  const [confirmModal, setConfirmModal] = useState(null);

  const images = (collectionPoint.images || []).filter(isUrl).length >= 3
    ? (collectionPoint.images || []).filter(isUrl).slice(0, 3)
    : FALLBACK_IMAGES;

  const [requestData, setRequestData] = useState({
    itemType: "",
    weight: 0.5,
    quantity: 1,
    condition: "New",
    brandModel: "",
    estimatedPoints: 0,
    environmentalImpact: 0,
  });

  const conditionMultipliers = { New: 1.0, Used: 0.7, Damaged: 0.4 };

  // Realistic flat base points per device category
  const deviceBasePoints = {
    'Mobile Phone': 60,
    'Laptop': 200,
    'Monitor': 150,
    'Desktop PC': 250,
    'Tablet': 100,
    'TV': 180,
    'Printer': 80,
    'Battery': 20,
    'Cables/Chargers': 15,
    'Camera': 70,
    'Gaming Console': 120,
    'Keyboard/Mouse': 25,
    'Router/Modem': 40,
    'Other': 35,
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [images.length]);

  const calculateEstimatedPoints = () => {
    const { itemType, condition, quantity } = requestData;
    // Use device lookup first, then fall back to the collection point's own value, then a sensible default
    const cpPoints = collectionPoint.acceptedItems.find(i => i.name === itemType)?.points;
    const basePoints = deviceBasePoints[itemType] || cpPoints || 35;
    const conditionMultiplier = conditionMultipliers[condition] || 1;
    return Math.round(basePoints * conditionMultiplier * quantity);
  };

  const calculateEnvironmentalImpact = () => {
    const { weight, condition, quantity } = requestData;
    const conditionImpact = conditionMultipliers[condition] || 1;
    return Math.min(5, Math.max(1, Math.round(weight * quantity * conditionImpact / 2)));
  };

  const estimatedPoints = calculateEstimatedPoints();
  const environmentalImpact = calculateEnvironmentalImpact();

  const handleInputChange = (field, value) => {
    setRequestData((prev) => ({ ...prev, [field]: value }));
  };

  const visibleItems = collectionPoint.acceptedItems.slice(0, visibleCount);

  const handleGetDirections = ([longitude, latitude]) => {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`, '_blank');
  };

  const showConfirm = (message, onConfirm) => {
    setConfirmModal({ message, onConfirm });
  };

  const handleBookmarkToggle = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      showConfirm(
        "You need an account to save favourites. Go to the login page?",
        () => { window.location.href = '/login'; }
      );
      return;
    }
    try {
      if (bookmarkStatus) {
        await API.delete(`/users/bookmark/${collectionPoint._id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast('Removed from saved centres', 'info');
        if (updateBookmarks) updateBookmarks(collectionPoint._id, false);
      } else {
        await API.post("/users/bookmark",
          { collectionPointId: collectionPoint._id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast('♥ Saved to your favourites!', 'success');
        if (updateBookmarks) updateBookmarks(collectionPoint._id, true);
      }
      setBookmarkStatus(!bookmarkStatus);
    } catch (error) {
      const msg = error.response?.data?.error || '';
      if (error.response?.status === 401 || msg.toLowerCase().includes('token')) {
        showConfirm(
          "Your session has expired. Please log in again.",
          () => { localStorage.removeItem('token'); window.location.href = '/login'; }
        );
      } else {
        toast(msg || "Failed to update bookmark. Please try again.", 'error');
      }
    }
  };

  const phone = collectionPoint.collectionPointMobileNumber || collectionPoint.activeCompanyNumber || '';
  const email = collectionPoint.collectionPointEmail || collectionPoint.activeCompanyEmail || '';
  const hours = collectionPoint.hours || 'See website for hours';

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-400 bg-opacity-30 flex items-end sm:items-start justify-center sm:justify-start">

      {/* Confirm Modal */}
      {confirmModal && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center px-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <p className="text-sm text-[#172A3A] font-medium mb-5 leading-relaxed">{confirmModal.message}</p>
            <div className="flex gap-3">
              <button
                onClick={() => { confirmModal.onConfirm(); setConfirmModal(null); }}
                className="flex-1 bg-[#29752B] text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-[#18421B] transition"
              >
                Continue
              </button>
              <button
                onClick={() => setConfirmModal(null)}
                className="flex-1 bg-gray-100 text-gray-600 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-200 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className="bg-white w-full sm:w-80 sm:ml-3 md:w-[400px] lg:w-[450px] md:ml-[7rem] sm:mt-[4.1rem] rounded-t-3xl sm:rounded-3xl overflow-hidden flex flex-col max-h-[82vh] sm:max-h-[calc(100vh-5rem)]"
      >
        {/* Mobile drag handle */}
        <div className="sm:hidden flex justify-center pt-2.5 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300"></div>
        </div>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 flex-shrink-0">
          <h3 className="text-sm font-bold text-[#34A853] leading-tight pr-2 line-clamp-1">{collectionPoint.name}</h3>
          <button
            onClick={onClose}
            className="close flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 text-sm font-bold"
          >
            ✕
          </button>
          <Tooltip anchorSelect=".close" place="top" style={{ zIndex: 10000 }}>Close</Tooltip>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto custom-scrollbar px-4 pb-4 flex-1">

          {/* Image slideshow */}
          <div className="relative mt-3 mb-3 rounded-xl overflow-hidden bg-gray-100" style={{ height: 150 }}>
            <div
              className="flex h-full transition-transform duration-500 ease-in-out"
              style={{
                transform: `translateX(-${currentSlide * (100 / images.length)}%)`,
                width: `${images.length * 100}%`
              }}
            >
              {images.map((src, i) => (
                <div key={i} className="h-full flex-shrink-0" style={{ width: `${100 / images.length}%` }}>
                  <img
                    src={src}
                    alt={`${collectionPoint.name} ${i + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = FALLBACK_IMAGES[i % FALLBACK_IMAGES.length]; }}
                  />
                </div>
              ))}
            </div>
            {/* Dots */}
            <div className="absolute bottom-1.5 left-0 right-0 flex justify-center gap-1">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentSlide(i)}
                  className={`h-1.5 rounded-full transition-all ${i === currentSlide ? 'bg-white w-4' : 'bg-white/60 w-1.5'}`}
                />
              ))}
            </div>
            {/* Bookmark */}
            <button
              data-tooltip-id="bookmark-tip"
              data-tooltip-content={bookmarkStatus ? "Remove from favourites" : "Add to favourites"}
              onClick={handleBookmarkToggle}
              className={`absolute top-2 right-2 p-1.5 rounded-full shadow z-10 transition ${bookmarkStatus ? 'bg-black' : 'bg-white/80 hover:bg-white'}`}
            >
              <img src="/images/material-symbols_bookmark.png" alt="Bookmark" className="w-3.5 h-3.5" />
            </button>
            <Tooltip id="bookmark-tip" style={{ zIndex: 10000 }} />
          </div>

          {/* Centre details */}
          <div className="space-y-1.5 text-xs mb-3">
            <div className="flex items-start gap-1.5">
              <img src="/images/location-icon3.png" alt="Location" className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span><span className="text-[#31705F] font-semibold">Address: </span><span className="text-gray-700">{collectionPoint.address}</span></span>
            </div>
            <div className="flex items-start gap-1.5">
              <img src="/images/clock-icon.png" alt="Hours" className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span><span className="text-[#31705F] font-semibold">Hours: </span><span className="text-gray-700">{hours}</span></span>
            </div>
            <div className="flex items-start gap-1.5">
              <img src="/images/call-icon.png" alt="Phone" className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>
                <span className="text-[#31705F] font-semibold">Phone: </span>
                <a href={`tel:${phone}`} className="text-gray-700 hover:text-[#34A853]">{phone || 'See website'}</a>
              </span>
            </div>
            <div className="flex items-start gap-1.5">
              <img src="/images/sms-tracking.png" alt="Email" className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>
                <span className="text-[#31705F] font-semibold">Email: </span>
                <a href={`mailto:${email}`} className="text-gray-700 hover:text-[#34A853] break-all">{email}</a>
              </span>
            </div>
            <div className="flex items-start gap-1.5">
              <img src="/images/devices-icon.png" alt="Devices" className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>
                <span className="text-[#31705F] font-semibold">Accepted: </span>
                <span className="text-gray-700">
                  {visibleItems.map((item, i) => (
                    <span key={i}>{item.name}{i < visibleItems.length - 1 ? ' | ' : ''}</span>
                  ))}
                </span>
                {visibleCount < collectionPoint.acceptedItems.length ? (
                  <button onClick={() => setVisibleCount(v => Math.min(v + 4, collectionPoint.acceptedItems.length))} className="text-[#31705F] hover:underline text-xs ml-1">+more</button>
                ) : visibleCount > 4 ? (
                  <button onClick={() => setVisibleCount(4)} className="text-[#31705F] hover:underline text-xs ml-1">less</button>
                ) : null}
              </span>
            </div>
          </div>

          {/* Directions button */}
          <button
            className="bg-[#34A853] text-white font-semibold py-2 px-4 rounded-xl w-full mb-3 hover:bg-[#206A29] transition-colors text-sm"
            onClick={() => handleGetDirections(collectionPoint.location.coordinates)}
          >
            Get Directions
          </button>

          <hr className="border-gray-200 mb-3" />

          {/* Reward estimate calculator */}
          <div className="bg-[#F0FBF4] rounded-xl p-3 border border-[#C1E8CD]">
            <h4 className="text-[#1B5E20] font-bold text-xs mb-0.5">🎁 Get Your Reward Estimate</h4>
            <p className="text-[10px] text-gray-500 mb-2">See how many points your devices are worth.</p>

            <div className="space-y-1.5">
              <div>
                <label className="block text-[10px] font-medium text-[#414042] mb-0.5">Device Type</label>
                <select
                  value={requestData.itemType}
                  onChange={(e) => handleInputChange("itemType", e.target.value)}
                  className="w-full border border-gray-300 px-2 py-1.5 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#29752B] text-xs bg-white"
                >
                  <option value="">Select device</option>
                  {collectionPoint.acceptedItems.map((item) => (
                    <option key={item.name} value={item.name}>{item.name} (+{item.points} pts)</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-3 gap-1.5">
                <div>
                  <label className="block text-[10px] font-medium text-[#414042] mb-0.5">Condition</label>
                  <select
                    value={requestData.condition}
                    onChange={(e) => handleInputChange("condition", e.target.value)}
                    className="w-full border border-gray-300 px-2 py-1.5 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#29752B] text-xs bg-white"
                  >
                    <option value="New">New</option>
                    <option value="Used">Used</option>
                    <option value="Damaged">Damaged</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-[#414042] mb-0.5">Qty</label>
                  <input
                    type="number" min={1} max={20}
                    value={requestData.quantity}
                    onChange={(e) => handleInputChange("quantity", parseInt(e.target.value) || 1)}
                    className="w-full border border-gray-300 px-2 py-1.5 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#29752B] text-xs"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-[#414042] mb-0.5">Weight (kg)</label>
                  <input
                    type="number" min={0.1} max={1000} step={0.1}
                    value={requestData.weight}
                    onChange={(e) => handleInputChange("weight", parseFloat(e.target.value) || 0.5)}
                    className="w-full border border-gray-300 px-2 py-1.5 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#29752B] text-xs"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-medium text-[#414042] mb-0.5">Brand / Model</label>
                <input
                  type="text"
                  value={requestData.brandModel}
                  placeholder="e.g. Apple iPhone 12"
                  onChange={(e) => handleInputChange("brandModel", e.target.value)}
                  className="w-full border border-gray-300 px-2 py-1.5 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#29752B] text-xs"
                />
              </div>
            </div>

            <div className="mt-2 flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-[#C1E8CD]">
              <span className="text-[10px] text-gray-600">Estimated reward</span>
              <span className="text-lg font-bold text-[#1B5E20]">
                <CountUpWrapper start={0} end={estimatedPoints} duration={1.5} redraw={true} />
                <span className="text-xs font-semibold ml-1">pts</span>
              </span>
            </div>
            <p className="text-[9px] text-gray-400 mt-1">*Estimate only. Final reward confirmed at the centre.</p>
          </div>

          {/* Place recycling request */}
          <button
            className="bg-[#34A853] text-white font-semibold py-2.5 px-4 rounded-xl w-full mt-3 hover:bg-[#206A29] transition-colors text-sm"
            onClick={() => setShowRecyclingPopup(true)}
          >
            Place Recycling Request
          </button>
        </div>
      </div>

      {showRecyclingPopup && (
        <RecyclingSubmissionPopup
          collectionPoint={collectionPoint}
          closePopup={() => setShowRecyclingPopup(false)}
          initialData={{ ...requestData }}
          estimatedPoints={estimatedPoints}
          environmentalImpact={environmentalImpact}
          pendingItems={pendingItems}
        />
      )}
    </div>
  );
};

export default SubmitRequestPopup;
